/**
 * @fileoverview Servicio para gestionar el envío de formularios de contacto
 * @author Jonatan Bianchi - Bianchi Desarrollo Web
 * @website https://jonatan.bianchi.com.ar
 * @email jonatan@bianchi.com.ar
 * @company Bianchi Desarrollo Web
 * @website https://www.bianchi.com.ar
 * @email info@bianchi.com.ar
 * @copyright 2025 Bianchi Desarrollo Web
 */

import { inject, Injectable } from '@angular/core';
import { Firestore, collection, addDoc, Timestamp } from '@angular/fire/firestore';
import { Observable, from, catchError, throwError } from 'rxjs';

export interface ContactFormData {
  nombre: string;
  email: string;
  asunto?: string;
  mensaje: string;
  recaptchaToken?: string;
}

/**
 * Servicio para gestionar contactos
 * Guarda los datos del formulario en Firestore con validaciones de seguridad
 */
@Injectable({
  providedIn: 'root'
})
export class ContactoService {
  private firestore: Firestore = inject(Firestore);

  /**
   * Guarda los datos del formulario de contacto en Firestore
   * Incluye validación de reCAPTCHA y sanitización de datos
   * @param formData Datos del formulario (debe incluir recaptchaToken)
   * @returns Observable que emite el ID del documento creado
   */
  guardarContacto(formData: ContactFormData): Observable<string> {
    try {
      const contactosRef = collection(this.firestore, 'contactos');
      
      if (!formData.nombre || !formData.email || !formData.mensaje) {
        return throwError(() => new Error('Datos incompletos del formulario'));
      }

      if (!formData.recaptchaToken) {
        return throwError(() => new Error('Token de reCAPTCHA requerido. Por favor, recarga la página.'));
      }

      const nombre = formData.nombre.trim();
      const email = formData.email.trim().toLowerCase();
      const mensaje = formData.mensaje.trim();
      const asunto = formData.asunto?.trim() || '';

      if (nombre.length > 100) {
        return throwError(() => new Error('El nombre es demasiado largo (máximo 100 caracteres)'));
      }
      if (email.length > 254) {
        return throwError(() => new Error('El email es demasiado largo (máximo 254 caracteres)'));
      }
      if (mensaje.length > 5000) {
        return throwError(() => new Error('El mensaje es demasiado largo (máximo 5000 caracteres)'));
      }
      if (mensaje.length < 10) {
        return throwError(() => new Error('El mensaje debe tener al menos 10 caracteres'));
      }
      if (asunto.length > 200) {
        return throwError(() => new Error('El asunto es demasiado largo (máximo 200 caracteres)'));
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return throwError(() => new Error('El formato del email no es válido'));
      }

      const sanitize = (str: string): string => {
        return str.replace(/[<>]/g, '');
      };

      const contactoData = {
        nombre: sanitize(nombre),
        email: email,
        asunto: sanitize(asunto),
        mensaje: sanitize(mensaje),
        fecha: Timestamp.now(),
        leido: false,
        recaptchaValidado: true
      };

      return from(
        addDoc(contactosRef, contactoData)
          .then(doc => {
            return doc.id;
          })
          .catch(error => {
            throw new Error(this.getErrorMessage(error));
          })
      ).pipe(
        catchError(error => {
          return throwError(() => error);
        })
      );
    } catch (error) {
      return throwError(() => new Error('Error al procesar el formulario'));
    }
  }

  /**
   * Obtiene un mensaje de error amigable basado en el código de error de Firebase
   * @param error Error de Firebase
   * @returns Mensaje de error traducido
   */
  private getErrorMessage(error: any): string {
    const code = error?.code || '';
    const message = error?.message || '';
    
    if (message && message !== code) {
      return message;
    }
    
    switch (code) {
      case 'permission-denied':
        return 'No tienes permisos para realizar esta acción. Verifica las reglas de Firestore.';
      case 'unavailable':
        return 'El servicio no está disponible temporalmente. Por favor, intenta más tarde.';
      case 'failed-precondition':
        return 'Error de configuración. Verifica la configuración de Firebase.';
      default:
        return message || 'Error al guardar el contacto. Por favor, intenta nuevamente.';
    }
  }
}
